import {
  DCPBadgeDTO,
  DCPCarProductEquipmentDTO,
  DCPColorVariantsDTO,
  DCPFinancialInformationDTO,
  DCPProductTypeDTO,
  DCPValueUnitData,
  DCPValueUnitDTO,
  ExtendedSMSProduct,
  IEmicoData,
  SMSClassification,
  SMSEnumDTO,
  SMSImage,
  SMSPointOfService,
  SMSPrice,
  SMSStock,
} from '../../models/shared/ocb';

export enum BodyTypeCode {
  ALL_TERRAIN = 'ALL_TERRAIN',
  CABRIO = 'CABRIO',
  CHASSIS = 'CHASSIS',
  CHASSIS_CREW_CAB = 'CHASSIS_CREW_CAB',
  COUPE = 'COUPE',
  COMPACT = 'COMPACT',
  LIMOUSINE = 'LIMOUSINE',
  LONG_SEDAN = 'LONG_SEDAN',
  MARCO_POLO = 'MARCO_POLO',
  MIXTO = 'MIXTO',
  HATCHBACK = 'HATCHBACK',
  OFF_ROADER = 'OFF_ROADER',
  PICKUP = 'PICKUP',
  ROADSTER = 'ROADSTER',
  SHOOTING_BRAKE = 'SHOOTING_BRAKE',
  SPORTS_TOURER = 'SPORTS_TOURER',
  STATION = 'STATION',
  SUV = 'SUV',
  T_MODEL = 'T_MODEL',
  VAN = 'VAN',
  PANEL_VAN = 'PANEL_VAN',
  TOURER = 'TOURER',
  PEOPLE_CARRIER = 'PEOPLE_CARRIER',
  PLATFORM = 'PLATFORM',
  PLATFORM_CREW_CAB = 'PLATFORM_CREW_CAB',
}

export enum FuelTypeCode {
  DIESEL = 'DIESEL',
  DIESEL_ELECTRIC_PLUGIN_HYBRID = 'DIESEL_ELECTRIC_PLUGIN_HYBRID',
  PETROL_ELECTRIC_PLUGIN_HYBRID = 'PETROL_ELECTRIC_PLUGIN_HYBRID',
  ELECTRIC = 'ELECTRIC',
  PETROL = 'PETROL',
}

export enum StockType {
  IN_STOCK = 'IN_STOCK',
  COMING_SOON = 'COMING_SOON',
}

export enum VEHICLE_IMAGE_TYPE {
  EXTERIOR = 'EXTERIOR',
  INTERIOR = 'INTERIOR',
}

export enum CommercialVansDcpType {
  NCOS_B_COMVANS = 'NCOS_B_COMVANS',
  NCOS_D_COMVANS = 'NCOS_D_COMVANS',
}

export enum PrivateVehicleDcpType {
  NCOS_D_STANDARD = 'NCOS_D_STANDARD',
  NCOS_B_STANDARD = 'NCOS_B_STANDARD',
}

export enum DivisionCode {
  VAN = 'VAN',
  PC = 'PC',
}

export enum DivisionOverseasCode {
  Vans = 'Vans',
  Cars = 'Cars',
}

interface FormattedValue {
  formattedValue?: string;
}

export interface Label {
  label?: string;
}

export interface LabelDescription extends Label {
  description: string;
}

export interface FormattedData<T extends number | string | undefined> extends FormattedValue, Label {
  value?: T;
}

export interface FormattedDataUnit<T extends number | string> extends FormattedData<T> {
  unit?: string;
}

export interface Url {
  url: string;
}

export interface Configuration {
  group?: string;
  polish?: string;
  text?: string;
  image?: Url;
}

/**
 * Represents transmission information for a product.
 */
interface Transmission {
  /**
   * The category of the transmission.
   * @type {FormattedData<string>}
   * @memberof Transmission
   */
  transmissionCategory: FormattedData<string>;
  /**
   * The gears of the transmission.
   * @type {FormattedData<number>}
   * @memberof Transmission
   */
  gears: FormattedData<number>;
}

/**
 * Represents weight information for a car.
 */
interface Weights {
  /**
   * The unloaded weight of the car.
   * @type {FormattedDataUnit<number>}
   * @memberof Weights
   */
  unloadedWeight: FormattedDataUnit<number>;
  /**
   * The maximum weight of the car.
   * @type {FormattedDataUnit<number>}
   * @memberof Weights
   */
  maximumWeight: FormattedDataUnit<number>;
}

/**
 * Represents power information for a product.
 */
interface Power {
  /**
   * The combined value of the power.
   * @type {FormattedValue}
   * @memberof Power
   */
  combinedValue: FormattedValue;
  /**
   * The power output in kilowatts from the combustion engine.
   * @type {FormattedDataUnit<number>}
   * @memberof Power
   */
  combustionKw: FormattedDataUnit<number>;
  /**
   * The power output in horsepower from the combustion engine.
   * @type {FormattedDataUnit<number>}
   * @memberof Power
   */
  combustionHp: FormattedDataUnit<number>;
  /**
   * The power output in kilowatts from the electric motor.
   * @type {FormattedDataUnit<number>}
   * @memberof Power
   */
  electricKw: FormattedDataUnit<number>;
  /**
   * The power output in horsepower from the electric motor.
   * @type {FormattedDataUnit<number>}
   * @memberof Power
   */
  electricHp: FormattedDataUnit<number>;
  /**
   * The combined power output in kilowatts.
   * @type {FormattedDataUnit<number>}
   * @memberof Power
   */
  combinedKw: FormattedDataUnit<number>;
  /**
   * The combined power output in horsepower.
   * @type {FormattedDataUnit<number>}
   * @memberof Power
   */
  combinedHp: FormattedDataUnit<number>;
}

/**
 * Represents engine information for a product.
 */
interface Engine {
  /**
   * The drive concept of the engine.
   * @type {FormattedData<string>}
   * @memberof Engine
   */
  driveConcept?: FormattedData<string>;
  /**
   * The number of cylinders in the engine.
   * @type {FormattedData<number>}
   * @memberof Engine
   */
  cylinder: FormattedData<number>;
  /**
   * The torque of the engine.
   * @type {FormattedDataUnit<number>}
   * @memberof Engine
   */
  torque: FormattedDataUnit<number>;
  /**
   * The fuel type of the engine.
   * @type {FormattedData<string>}
   * @memberof Engine
   */
  fuelType?: FormattedData<string>;
  /**
   * The type of propulsion of the engine.
   * @type {FormattedData<string>}
   * @memberof Engine
   */
  typeOfPropulsion: FormattedData<string>;
  /**
   * The power output of the engine.
   * @type {Power}
   * @memberof Engine
   */
  power: Power;
}

/**
 * Represents technical information for a product.
 */
export interface TechnicalInformation {
  /**
   * The top speed of the product.
   * @type {FormattedDataUnit<number>}
   * @memberof TechnicalInformation
   */
  topSpeed: FormattedDataUnit<number>;
  /**
   * The acceleration of the product.
   * @type {FormattedDataUnit<number>}
   * @memberof TechnicalInformation
   */
  acceleration: FormattedDataUnit<number>;
  /**
   * The number of seats in the product.
   * @type {FormattedData<number>}
   * @memberof TechnicalInformation
   */
  seats: FormattedData<number>;
  /**
   * The number of doors in the product.
   * @type {FormattedData<number>}
   * @memberof TechnicalInformation
   */
  doors: FormattedData<number>;
  /**
   * The transmission of the product.
   * @type {Transmission}
   * @memberof TechnicalInformation
   */
  transmission: Transmission;
  /**
   * The payload capacity of the product.
   * @type {FormattedDataUnit<number>}
   * @memberof TechnicalInformation
   */
  payload: FormattedDataUnit<number>;
  /**
   * The weights of the product.
   * @type {Weights}
   * @memberof TechnicalInformation
   */
  weights: Weights;
  /**
   * The dimensions of the product.
   * @type {FormattedDataUnit<number>}
   * @memberof TechnicalInformation
   */
  dimension: FormattedDataUnit<number>;
  /**
   * The engine information of the product.
   * @type {Engine}
   * @memberof TechnicalInformation
   */
  engine: Engine;
}

interface Identification {
  code: string;
  commissionNumber: string;
  vin?: string;
  variantId?: string;
  productType?: string;
  productId?: string;
}

/**
 * Represents an EMICO attribute.
 */
export interface EmicoAttribute {
  /**
   * The ID of the EMICO attribute.
   * @type {string}
   * @memberof EmicoAttribute
   */
  id: string;
  /**
   * The value of the EMICO attribute.
   * @type {string}
   * @memberof EmicoAttribute
   */
  value: string;
  /**
   * The unit of the EMICO attribute.
   * @type {string}
   * @memberof EmicoAttribute
   */
  unit: string;
  /**
   * The label of the EMICO attribute.
   * @type {string}
   * @memberof EmicoAttribute
   */
  label: string;
  /**
   * The display value of the EMICO attribute.
   * @type {string}
   * @memberof EmicoAttribute
   */
  displayValue: string;
  /**
   * The list of contexts in which this EMICO attribute should be displayed.
   * @type {string[]}
   * @memberof EmicoAttribute
   */
  mustshowin: string[];
  displayImage: Url;
}

export interface EmicoItem {
  /**
   * The list of attributes associated with the EMICO data.
   * @type {EmicoAttribute[]}
   * @memberof Emico
   */
  attributes: EmicoAttribute[];
  /**
   * The footnotes associated with the EMICO data.
   * @type {string}
   * @memberof Emico
   */
  footnotes: string;
}

/**
 * Represents EMICO data.
 */
export interface Emico {
  wtlp: EmicoItem;
  nefz: EmicoItem;
}

interface VehicleModel {
  name: string;
  baumuster: string;
  nationalSalesType?: string;
  modelYearCode: string;
  modelYear: number | string;
  changeYearCode?: string;
  bodyType?: FormattedData<string>;
  vehicleClass?: FormattedData<string>;
  brand?: FormattedData<string>;
  productGroup?: FormattedData<string>;
}

interface Characteristics {
  highlights?: Label[];
  packages?: Label[];
  lines?: Label[];
  componentGroups?: Label[];
  badges?: LabelDescription[];
}

export interface LabelDescription extends Label {
  description: string;
}

export interface Url {
  url: string;
}

export interface Configuration {
  group?: string;
  polish?: string;
  text?: string;
  image?: Url;
}

export interface Images {
  exterior: Url[];
  interior: Url[];
  default?: Url;
}

export interface ProductDetail {
  vendor?: { pointOfServices: Required<SMSPointOfService>[] };
  assortment: string;
  baseId: string;
  __originalVehicleModel: ExtendedSMSProduct;
  carEquipments: Required<DCPCarProductEquipmentDTO>[];
  // TODO: classifications still needed? only used as fallback
  characteristics: Characteristics;
  classifications: Required<SMSClassification>[];
  color: Configuration;
  colorVariants: DCPColorVariantsDTO[];
  combustionEngineSize: DCPValueUnitDTO;
  // only dcp.ProductType.name is used
  dcpProductType: Required<DCPProductTypeDTO>;
  discountPrice: SMSPrice;
  division: string;
  engineSize: DCPValueUnitDTO;
  engineSizeInCcm: number;
  engineType: string;
  fiscalValue: Required<DCPFinancialInformationDTO>;
  frontTyreSize: string;
  fuelTankCapacity: DCPValueUnitDTO;
  height: DCPValueUnitDTO;
  identification: Identification;
  images: Images;
  length: DCPValueUnitDTO;
  listPrice: Required<SMSPrice>;
  loadingSpaceHeight: DCPValueUnitDTO;
  loadingSpaceLength: DCPValueUnitDTO;
  loadingSpaceVolume: DCPValueUnitDTO;
  loadingSpaceWidth: DCPValueUnitDTO;
  localEmissionAndConsumptionData: string;
  mileage: DCPValueUnitDTO;
  model: string;
  monthlyRate: Required<DCPFinancialInformationDTO>;
  numberOfReviews: number;
  pointOfServiceCode: string;
  pointOfServiceDisplayName: string;
  pointOfServiceFormattedAddress: string;
  pointOfServices: SMSPointOfService[];
  price: Required<SMSPrice>;
  primaryImage: SMSImage;
  rearTyreSize: string;
  reservationFee: SMSPrice;
  source: string;
  steeringWheel: SMSEnumDTO;
  stock: SMSStock;
  upholstery: Configuration;
  vehicleModel: VehicleModel;
  volume: DCPValueUnitData;
  wholeSale: boolean;
  summary?: string;
  /**
   * The technical information of the product or equipment
   * @type {TechnicalInformation}
   * @memberof ProductDetail
   */
  technicalInformation: TechnicalInformation;
  emico: Emico;
  powerInKw: number;
}
